import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Paprika, PaprikaRadius, PaprikaSpacing, PaprikaTypography } from '../../styles/theme';
import {
  WeekSlot,
  getTopSuggestions,
  ScoredSuggestion,
  DietaryConfig,
  SuggestionBadge,
  isVegetarianRecipe,
  isFishRecipe,
  knownCookTime,
} from '../../services/weekGeneratorService';
import { PersonalRecipeIndexEntry } from '../../services/personalRecipeIndex';
import { RecipeMetadata } from '../../redux/recipeMetadataSlice';
import SuggestionCard from './SuggestionCard';
import { useSuggestionPool } from '../../hooks/useSuggestionPool';
import { useTranslation } from 'react-i18next';
import { formatWeekday } from '../../utils/dateLocale';

type SheetStep = 'slot-selection' | 'suggestions';

interface RecipeSuggestionSheetProps {
  visible: boolean;
  slot: WeekSlot | null;
  emptySlots: WeekSlot[];
  allRecipes: PersonalRecipeIndexEntry[];
  currentWeekRecipeIds: string[];
  previousWeekRecipeIds: string[];
  metadata: Record<string, RecipeMetadata>;
  dietaryConfig?: DietaryConfig;
  onSelectRecipe: (recipe: PersonalRecipeIndexEntry, slot: WeekSlot) => void;
  onBrowseAll: () => void;
  onAddFreeText?: (slot: WeekSlot) => void;
  onClose: () => void;
}

const INITIAL_COUNT = 5;
const EXPANDED_COUNT = 10;

// Badge filter labels
/** Rendered with t(). */
const FILTER_LABEL_KEYS: Record<SuggestionBadge['type'], string> = {
  quick: 'thisWeek.suggestionFilters.quick',
  veggie: 'thisWeek.suggestionFilters.veggie',
  fish: 'thisWeek.suggestionFilters.fish',
  favorite: 'thisWeek.suggestionFilters.favorite',
  new: 'thisWeek.suggestionFilters.new',
  cuisine: 'thisWeek.suggestionFilters.cuisine',
};

export const RecipeSuggestionSheet: React.FC<RecipeSuggestionSheetProps> = ({
  visible,
  slot: initialSlot,
  emptySlots,
  allRecipes,
  currentWeekRecipeIds,
  previousWeekRecipeIds,
  metadata,
  dietaryConfig,
  onSelectRecipe,
  onBrowseAll,
  onAddFreeText,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  // Sheet state
  const [step, setStep] = useState<SheetStep>('slot-selection');
  const [selectedSlot, setSelectedSlot] = useState<WeekSlot | null>(initialSlot);
  const [showMore, setShowMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState<SuggestionBadge['type'] | null>(null);

  // Reset state when sheet opens/closes
  React.useEffect(() => {
    if (visible) {
      // If there's only one empty slot, skip to suggestions
      if (emptySlots.length === 1) {
        setSelectedSlot(emptySlots[0]);
        setStep('suggestions');
      } else if (initialSlot) {
        setSelectedSlot(initialSlot);
        setStep('suggestions');
      } else {
        setStep('slot-selection');
        setSelectedSlot(null);
      }
    } else {
      setShowMore(false);
      setActiveFilter(null);
    }
  }, [visible, emptySlots, initialSlot]);

  // Allergies, diets and the same-meal rules first: `getTopSuggestions` knows none of them.
  const pool = useSuggestionPool(allRecipes, selectedSlot?.date ?? null, undefined, selectedSlot?.mealType);

  // Get suggestions when sheet is visible and slot is selected
  const allSuggestionsResult = useMemo(() => {
    if (!visible || !selectedSlot || pool.length === 0) {
      return { suggestions: [] as ScoredSuggestion[], usedFallback: false };
    }

    return getTopSuggestions(
      selectedSlot,
      pool,
      currentWeekRecipeIds,
      previousWeekRecipeIds,
      metadata,
      dietaryConfig,
      EXPANDED_COUNT
    );
  }, [visible, selectedSlot, pool, currentWeekRecipeIds, previousWeekRecipeIds, metadata, dietaryConfig]);

  const allSuggestions = allSuggestionsResult.suggestions;

  // Filter suggestions by active filter
  const filteredSuggestionsResult = useMemo(() => {
    if (!activeFilter) return allSuggestionsResult;

    // For filter mode, get more recipes and apply filter
    const filterableRecipes = pool.filter(recipe => {
      // Don't include recipes already in the week
      if (currentWeekRecipeIds.includes(recipe.id)) return false;

      switch (activeFilter) {
        case 'quick': {
          // A recipe with no time is not a 20-minute one — 0 means unknown.
          const minutes = knownCookTime(recipe);
          return minutes !== null && minutes <= 20;
        }
        case 'veggie':
          return isVegetarianRecipe(recipe);
        case 'fish':
          return isFishRecipe(recipe);
        case 'favorite':
          return (metadata[recipe.id]?.rating || 0) >= 4;
        case 'new':
          return !metadata[recipe.id] || (metadata[recipe.id].timesPlanned || 0) === 0;
        default:
          return true;
      }
    });

    // Score and sort filtered recipes
    if (!selectedSlot) {
      return { suggestions: [] as ScoredSuggestion[], usedFallback: false };
    }

    return getTopSuggestions(
      selectedSlot,
      filterableRecipes,
      currentWeekRecipeIds,
      previousWeekRecipeIds,
      metadata,
      dietaryConfig,
      EXPANDED_COUNT
    );
  }, [activeFilter, allSuggestionsResult, pool, selectedSlot, currentWeekRecipeIds, previousWeekRecipeIds, metadata, dietaryConfig]);

  const suggestions = activeFilter ? filteredSuggestionsResult.suggestions : allSuggestions;
  const usedFallback = activeFilter ? filteredSuggestionsResult.usedFallback : allSuggestionsResult.usedFallback;

  const displayedSuggestions = showMore
    ? suggestions
    : suggestions.slice(0, INITIAL_COUNT);

  const hasMoreSuggestions = suggestions.length > INITIAL_COUNT && !showMore;

  // Handle slot selection
  const handleSelectSlot = (slot: WeekSlot) => {
    setSelectedSlot(slot);
    setStep('suggestions');
  };

  // Handle back navigation
  const handleBack = () => {
    if (activeFilter) {
      setActiveFilter(null);
    } else {
      setStep('slot-selection');
      setSelectedSlot(null);
    }
  };

  // Handle badge press - filter by that type
  const handleBadgePress = (badgeType: SuggestionBadge['type']) => {
    setActiveFilter(badgeType);
    setShowMore(false);
  };

  // Handle recipe selection
  const handleSelectRecipe = (recipe: PersonalRecipeIndexEntry) => {
    if (selectedSlot) {
      onSelectRecipe(recipe, selectedSlot);
    }
  };

  // slot.dayName is the scorer's English key, never copy: these read the date.
  const mealLabel = (slot: WeekSlot) => t(`thisWeek.mealTypes.${slot.mealType}`);

  // "Thursday Lunch" / « Dîner du jeudi »
  const formatSlotTitle = (slot: WeekSlot) =>
    t('thisWeek.slotTitle', { day: formatWeekday(slot.date, i18n.language), meal: mealLabel(slot) });

  // Render slot selection step
  const renderSlotSelection = () => (
    <>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.slotTitle}>{t('thisWeek.planAMeal')}</Text>
          <Text style={styles.title}>{t('thisWeek.selectTimeSlot')}</Text>
        </View>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="close" size={24} color={Paprika.textSecondary} />
        </TouchableOpacity>
      </View>

      {/* Slot list */}
      <ScrollView style={styles.slotList} showsVerticalScrollIndicator={false}>
        {emptySlots.map((slot, index) => (
          <TouchableOpacity
            key={`${slot.date}-${slot.mealType}`}
            style={styles.slotRow}
            onPress={() => handleSelectSlot(slot)}
            activeOpacity={0.7}
          >
            <View style={styles.slotInfo}>
              <Text style={styles.slotDay}>{formatWeekday(slot.date, i18n.language)}</Text>
              <Text style={styles.slotMeal}>{mealLabel(slot)}</Text>
            </View>
            <View style={styles.slotArrow}>
              <Ionicons name="chevron-forward" size={20} color={Paprika.textMuted} />
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Browse all button */}
      <TouchableOpacity
        style={styles.browseAllButton}
        onPress={onBrowseAll}
      >
        <Text style={styles.browseAllText}>{t('thisWeek.browseAllRecipes')}</Text>
        <Ionicons name="arrow-forward" size={18} color={Paprika.accent} />
      </TouchableOpacity>
    </>
  );

  // Render suggestions step
  const renderSuggestions = () => {
    if (!selectedSlot) return null;

    const slotTitle = formatSlotTitle(selectedSlot);
    const showBackButton = emptySlots.length > 1 || activeFilter;

    return (
      <>
        {/* Header with back button */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {showBackButton && (
              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBack}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="chevron-back" size={24} color={Paprika.textPrimary} />
              </TouchableOpacity>
            )}
            <View>
              <Text style={styles.title}>
                {activeFilter
                  ? t('thisWeek.filteredFor', { filter: t(FILTER_LABEL_KEYS[activeFilter]) })
                  : t('thisWeek.suggestedFor')}
              </Text>
              <Text style={styles.slotTitle} testID="suggestion-slot-title">{slotTitle}</Text>
            </View>
          </View>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="close" size={24} color={Paprika.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Active filter chip */}
        {activeFilter && (
          <View style={styles.filterChipContainer}>
            <TouchableOpacity
              style={styles.filterChip}
              onPress={() => setActiveFilter(null)}
            >
              <Text style={styles.filterChipText}>{t(FILTER_LABEL_KEYS[activeFilter])}</Text>
              <Ionicons name="close-circle" size={16} color={Paprika.accent} />
            </TouchableOpacity>
          </View>
        )}

        {/* Add custom meal button */}
        {onAddFreeText && selectedSlot && !activeFilter && (
          <TouchableOpacity
            style={styles.freeTextButton}
            onPress={() => onAddFreeText(selectedSlot)}
          >
            <Feather name="edit-3" size={18} color={Paprika.accent} />
            <Text style={styles.freeTextButtonText}>{t('thisWeek.addCustomMeal')}</Text>
          </TouchableOpacity>
        )}

        {/* Fallback banner — shown when the strict suggestion pass returned
            nothing and we're showing recipes from the broader library instead. */}
        {usedFallback && suggestions.length > 0 && (
          <View style={styles.fallbackBanner}>
            <Feather name="info" size={14} color={Paprika.accent} />
            <Text style={styles.fallbackBannerText}>{t('thisWeek.suggestionFallback')}</Text>
          </View>
        )}

        {/* Suggestions */}
        {suggestions.length === 0 ? (
          <View style={styles.loadingContainer}>
            {allRecipes.length === 0 ? (
              <>
                <ActivityIndicator size="large" color={Paprika.accent} />
                <Text style={styles.loadingText}>{t('thisWeek.findingRecipes')}</Text>
              </>
            ) : (
              <>
                <Feather name="search" size={40} color={Paprika.textMuted} style={styles.emptyIcon} />
                <Text style={styles.emptyText}>
                  {activeFilter
                    ? t('thisWeek.noFilteredSuggestions', { filter: t(FILTER_LABEL_KEYS[activeFilter]) })
                    : t('thisWeek.noSuggestions')}
                </Text>
                {activeFilter && (
                  <TouchableOpacity
                    style={styles.clearFilterButton}
                    onPress={() => setActiveFilter(null)}
                  >
                    <Text style={styles.clearFilterText}>{t('thisWeek.showAllSuggestions')}</Text>
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        ) : (
          <>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.suggestionsContainer}
            >
              {displayedSuggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.recipe.id}
                  recipe={suggestion.recipe}
                  badges={suggestion.badges}
                  onPress={() => handleSelectRecipe(suggestion.recipe)}
                  onBadgePress={handleBadgePress}
                />
              ))}
            </ScrollView>

            {/* See more button */}
            {hasMoreSuggestions && (
              <TouchableOpacity
                style={styles.seeMoreButton}
                onPress={() => setShowMore(true)}
              >
                <Text style={styles.seeMoreText}>
                  {t('thisWeek.seeMoreSuggestions', { count: suggestions.length - INITIAL_COUNT })}
                </Text>
                <Ionicons name="chevron-down" size={16} color={Paprika.accent} />
              </TouchableOpacity>
            )}
          </>
        )}

        {/* Browse all button */}
        <TouchableOpacity
          style={styles.browseAllButton}
          onPress={onBrowseAll}
        >
          <Text style={styles.browseAllText}>{t('thisWeek.browseAllRecipes')}</Text>
          <Ionicons name="arrow-forward" size={18} color={Paprika.accent} />
        </TouchableOpacity>
      </>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          {/* Drag indicator */}
          <View style={styles.dragIndicator} />

          {step === 'slot-selection' ? renderSlotSelection() : renderSuggestions()}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Paprika.background,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
    maxHeight: '70%',
  },
  dragIndicator: {
    width: 40,
    height: 4,
    backgroundColor: Paprika.borderLight,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.md,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  backButton: {
    marginRight: PaprikaSpacing.sm,
    padding: PaprikaSpacing.xs,
  },
  title: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  slotTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginTop: 2,
  },
  closeButton: {
    padding: PaprikaSpacing.xs,
  },

  // Slot selection styles
  slotList: {
    maxHeight: 300,
    paddingHorizontal: PaprikaSpacing.lg,
  },
  slotRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceLight,
    borderRadius: PaprikaRadius.md,
    marginBottom: PaprikaSpacing.sm,
  },
  slotInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
  },
  slotDay: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
    minWidth: 80,
  },
  slotMeal: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  slotArrow: {
    opacity: 0.5,
  },

  // Filter chip styles
  filterChipContainer: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.sm,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: Paprika.accentLight,
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: PaprikaSpacing.xs,
    borderRadius: PaprikaRadius.md,
    gap: 4,
  },
  filterChipText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },

  // Fallback banner
  fallbackBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: PaprikaSpacing.xs,
    backgroundColor: Paprika.accentLight,
    borderRadius: PaprikaRadius.sm,
  },
  fallbackBannerText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
    flex: 1,
  },

  // Loading/empty states
  loadingContainer: {
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.md,
  },
  emptyIcon: {
    marginBottom: PaprikaSpacing.md,
  },
  emptyText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  clearFilterButton: {
    marginTop: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
  },
  clearFilterText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },

  // Suggestions styles
  suggestionsContainer: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.sm,
  },
  seeMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: PaprikaSpacing.sm,
    gap: 4,
  },
  seeMoreText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },
  browseAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: PaprikaSpacing.lg,
    marginTop: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.accent,
    gap: 8,
  },
  browseAllText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.accent,
  },

  // Free-text / custom meal button
  freeTextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.accent,
    borderStyle: 'dashed',
    gap: 8,
  },
  freeTextButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.accent,
  },
});

export default RecipeSuggestionSheet;
